import { useEffect, useState } from "react";
import useDebounce from "effects/useDebounce";
import { v4 as uuidv4 } from "uuid";
import { openModal } from "lib/actions/modal";
import { showPopupFlag } from "lib/actions/userInterface";
import Apps from "lib/constants/Apps";
import Modals from "lib/constants/Modals";
import StorageModels from "lib/constants/StorageModels";
import handlePopupFlagError from "lib/errors/handlePopupFlagError";
import { MixAction, MixPanel } from "lib/mixpanel";
import { useDispatch } from "store";
import {
  fetchStorageDrawerAccessCounts,
  lockDevice,
  powerOffDevice,
  rebootDevice,
  refreshDeviceScreen,
  unlinkStorage,
  unlockDevice,
  updateStorage,
} from "store/reducers";
import { FixLater, TStorage, TStorageDrawer } from "types";
import { SecondaryButton, WarningButton } from "components/Buttons";
import { DataGrid, DataItem, DataItemGroup } from "components/DataGrid";
import { CheckboxInput, InputGroup, NumberInput, SelectInput, TextAreaInput, TextInput } from "components/Input";
import { RestrictByApp, useIsAdmin } from "components/Restricted";
import StageHeader from "components/StageHeader";
import { Paragraph, Text } from "components/Typography";
import FieldGroup from "components/form/FieldGroup/FieldGroup";
import { LocationSelector } from "components/inputs/LocationSelector/LocationSelector";
import SelectStorageModeInput from "components/inputs/SelectStorageModeInput";
import SelectStorageModelInput from "components/inputs/SelectStorageModelInput";
import { DrawerNames, VSpace } from "components/shared";
import {
  describeDrawerRemovalWarning,
  findRemovedDrawerValues,
} from "components/shared/features/storages/utils/drawerAccess.utility";

// The sentry contract puts no ceiling on the hold delay, so this is a sanity bound: a minute of
// held magnet is already far longer than any real drawer takes to close, and a mistyped value
// would leave drawers unlockable until someone noticed.
const MAGLOCK_DELAY_MAX_MS = 60000;

const TIMEZONE_OPTIONS = [
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "Europe/Dublin", label: "Europe/Dublin (GMT/IST)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "Europe/Amsterdam (CET/CEST)" },
  { value: "Europe/Brussels", label: "Europe/Brussels (CET/CEST)" },
  { value: "Europe/Madrid", label: "Europe/Madrid (CET/CEST)" },
  { value: "Europe/Rome", label: "Europe/Rome (CET/CEST)" },
  { value: "Europe/Zurich", label: "Europe/Zurich (CET/CEST)" },
  { value: "Europe/Stockholm", label: "Europe/Stockholm (CET/CEST)" },
  { value: "Europe/Oslo", label: "Europe/Oslo (CET/CEST)" },
  { value: "Europe/Helsinki", label: "Europe/Helsinki (EET/EEST)" },
  { value: "Europe/Athens", label: "Europe/Athens (EET/EEST)" },
  { value: "Europe/Bucharest", label: "Europe/Bucharest (EET/EEST)" },
  { value: "Europe/Istanbul", label: "Europe/Istanbul (TRT)" },
  { value: "Europe/Moscow", label: "Europe/Moscow (MSK)" },
  { value: "America/New_York", label: "America/New York (EST/EDT)" },
  { value: "America/Chicago", label: "America/Chicago (CST/CDT)" },
  { value: "America/Denver", label: "America/Denver (MST/MDT)" },
  { value: "America/Los_Angeles", label: "America/Los Angeles (PST/PDT)" },
  { value: "America/Anchorage", label: "America/Anchorage (AKST/AKDT)" },
  { value: "America/Toronto", label: "America/Toronto (EST/EDT)" },
  { value: "America/Vancouver", label: "America/Vancouver (PST/PDT)" },
  { value: "America/Sao_Paulo", label: "America/São Paulo (BRT)" },
  { value: "America/Argentina/Buenos_Aires", label: "America/Buenos Aires (ART)" },
  { value: "America/Mexico_City", label: "America/Mexico City (CST/CDT)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT)" },
  { value: "Asia/Hong_Kong", label: "Asia/Hong Kong (HKT)" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai (CST)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
  { value: "Asia/Seoul", label: "Asia/Seoul (KST)" },
  { value: "Asia/Bangkok", label: "Asia/Bangkok (ICT)" },
  { value: "Asia/Jakarta", label: "Asia/Jakarta (WIB)" },
  { value: "Asia/Riyadh", label: "Asia/Riyadh (AST)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "Australia/Melbourne (AEST/AEDT)" },
  { value: "Australia/Brisbane", label: "Australia/Brisbane (AEST)" },
  { value: "Australia/Perth", label: "Australia/Perth (AWST)" },
  { value: "Australia/Adelaide", label: "Australia/Adelaide (ACST/ACDT)" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland (NZST/NZDT)" },
  { value: "Pacific/Fiji", label: "Pacific/Fiji (FJT)" },
  { value: "Africa/Cairo", label: "Africa/Cairo (EET)" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (SAST)" },
  { value: "Africa/Lagos", label: "Africa/Lagos (WAT)" },
  { value: "Africa/Nairobi", label: "Africa/Nairobi (EAT)" },
  { value: "Africa/Casablanca", label: "Africa/Casablanca (WET/WEST)" },
  { value: "UTC", label: "UTC" },
  { value: "Pacific/Honolulu", label: "Pacific/Honolulu (HST)" },
];

type StorageSettingsPageProps = {
  storage: TStorage;
};

export const StorageSettingsPage = ({ storage }: StorageSettingsPageProps) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const [dirty, setDirty] = useState<boolean>(false);
  const [values, setValues] = useState<TStorage>(storage);
  const debouncedValues = useDebounce(values, 750);

  useEffect(() => {
    dirty && saveChanges();
  }, [debouncedValues]);

  // Applies a drawer-bearing patch, but — when the edit would drop a drawer number that an
  // access rule references and maglocks are enabled for this toolbox — only after the admin
  // confirms. The commit (setDirty + setValues) never runs until that decision is made, so the
  // 750ms autosave debounce below can't fire on a change the admin hasn't actually approved yet:
  // nothing is written to `values` for the debounce to pick up until `apply()` runs.
  const commitDrawersPatch = async (patch: Partial<TStorage> & { drawers: TStorageDrawer[] }) => {
    const apply = () => {
      setDirty(true);
      setValues((prevState) => ({ ...prevState, ...patch }));
    };

    const removedValues = findRemovedDrawerValues(values.drawers, patch.drawers);
    if (removedValues.length === 0 || !values.config?.maglockEnabled) {
      apply();
      return;
    }

    let counts: Record<number, number>;
    try {
      counts = await dispatch(fetchStorageDrawerAccessCounts(storage.id)).unwrap();
    } catch (err) {
      // Can't find out whether a rule depends on this drawer. The server reconciles orphaned
      // access_grant_drawers rows safely on its own either way, so fail open rather than block
      // an edit the admin has no way to unblock.
      handlePopupFlagError(err);
      apply();
      return;
    }

    const affected = removedValues
      .map((value) => ({ value, count: counts[value] ?? 0 }))
      .filter((drawer) => drawer.count > 0);

    if (affected.length === 0) {
      apply();
      return;
    }

    dispatch(
      openModal(Modals.CONFIRMATION, {
        title: "Remove drawer access?",
        description: describeDrawerRemovalWarning(affected),
        actionText: "Remove anyway",
        actionColor: "warning",
        onConfirm: apply,
      }),
    );
  };

  const handleChange = (name: FixLater, value: FixLater) => {
    if (name === "model") {
      // Ensure that the correct amount of drawers is saved when changing model
      const storageModel = StorageModels.resolveItem(value);
      if (!storageModel) {
        console.error(`failed to resolve storage model '${value}'`);
        return;
      }
      const newDrawers = new Array(storageModel.drawerCount);
      for (let i = 0; i < storageModel.drawerCount; i++) {
        const oldDrawer = values.drawers.length > i ? values.drawers[i] : null;
        newDrawers[i] = {
          id: uuidv4(),
          name: oldDrawer?.name,
          value: i + 1,
          order: i,
          fullSvg: oldDrawer?.fullSvg,
          svgPath: oldDrawer?.svgPath,
          irSensorBoardId: oldDrawer?.irSensorBoardId,
          irSensorThreshold: oldDrawer?.irSensorThreshold,
          // A model change is the same physical box, so the loom has not changed. Dropping the
          // maglock number would leave restricted drawers with no magnet, and the device logs an
          // error and opens the drawer anyway: the restriction would survive in the database and
          // silently stop being real.
          maglockNumber: oldDrawer?.maglockNumber,
        };
      }
      commitDrawersPatch({ model: value, drawers: newDrawers });
      return;
    }

    if (name === "drawers") {
      commitDrawersPatch({ drawers: value });
      return;
    }

    setDirty(true);
    setValues((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSelectChange = (name: string, value: FixLater) => {
    handleChange(name, value.value);
  };

  const changeLocation = (name: string, value: FixLater) => {
    handleChange(name, value?.id);
  };

  const handleConfigSelectChange = (name: string, value: FixLater) => {
    setDirty(true);
    setValues((prev) => ({
      ...prev,
      config: {
        ...prev.config!,
        [name]: value?.value ?? "",
      },
    }));
  };

  // For config inputs that already carry their own raw value (a toggle's checked state, a
  // number input's parsed number) rather than a react-select option object.
  // Clearing the field gives parseFloat("") === NaN, which would be sent as null and read back
  // as a missing delay. Zero is the same value the API clamps a negative delay to.
  const handleMaglockDelayChange = (name: string, value: FixLater) => {
    handleConfigChange(name, Number.isNaN(value) ? 0 : value);
  };

  const handleConfigChange = (name: string, value: FixLater) => {
    setDirty(true);
    setValues((prev) => ({
      ...prev,
      config: {
        ...prev.config!,
        [name]: value,
      },
    }));
  };

  const saveChanges = async () => {
    try {
      await dispatch(
        updateStorage({
          ...storage,
          ...values,
        }),
      ).unwrap();
      dispatch(
        showPopupFlag({
          appearance: "success",
          title: "Settings saved",
        }),
      );
    } catch (err) {
      handlePopupFlagError(err);
    }
  };

  // The console is a full page in its own tab so the admin keeps this settings page open.
  const openShell = () => {
    window.open(`/apps/storage/toolboxes/${storage.id}/shell`, "_blank", "noopener");
  };

  // Opens in its own tab so the admin keeps this settings page open, matching Open shell.
  const openSensorHealth = () => {
    window.open(`/apps/storage/toolboxes/${storage.id}/sensors`, "_blank", "noopener");
  };

  const handleUnlinkStorage = () => {
    dispatch(
      openModal(Modals.CONFIRMATION, {
        description:
          "Are you sure you want to unlink this toolbox? Doing this will return your physical toolbox to factory settings.",
        actionText: "Unlink toolbox",
        actionColor: "warning",
        onConfirm: async () => {
          try {
            await dispatch(unlinkStorage(storage.id)).unwrap();
            MixPanel.track(MixAction.RemoteControlUnlinkDevice, {
              storageId: storage.id,
            });
            dispatch(
              showPopupFlag({
                appearance: "success",
                title: "Storage unlinked",
              }),
            );
          } catch (err) {
            handlePopupFlagError(err);
          }
        },
      }),
    );
  };

  const lock = () => {
    return sendRemoteControlCommand(
      async () => {
        try {
          await dispatch(
            lockDevice({
              storageId: storage.id,
            }),
          ).unwrap();
          dispatch(
            showPopupFlag({
              appearance: "success",
              title: "Device will lock shortly",
            }),
          );
        } catch (err) {
          handlePopupFlagError(err);
        }
      },
      "Are you sure you want to lock this device?",
      "Lock device",
    );
  };

  const unlock = () => {
    return sendRemoteControlCommand(
      async () => {
        try {
          await dispatch(
            unlockDevice({
              storageId: storage.id,
            }),
          ).unwrap();
          dispatch(
            showPopupFlag({
              appearance: "success",
              title: "Device will unlock shortly",
            }),
          );
        } catch (err) {
          handlePopupFlagError(err);
        }
      },
      "Are you sure you want to unlock this device?",
      "Unlock device",
    );
  };

  const reboot = () => {
    return sendRemoteControlCommand(
      async () => {
        try {
          await dispatch(
            rebootDevice({
              storageId: storage.id,
            }),
          ).unwrap();
          dispatch(
            showPopupFlag({
              appearance: "success",
              title: "Device will reboot shortly",
            }),
          );
        } catch (err) {
          handlePopupFlagError(err);
        }
      },
      "Are you sure you want to reboot this device?",
      "Reboot device",
    );
  };

  const powerOff = () => {
    return sendRemoteControlCommand(
      async () => {
        try {
          await dispatch(
            powerOffDevice({
              storageId: storage.id,
            }),
          ).unwrap();
          dispatch(
            showPopupFlag({
              appearance: "success",
              title: "Device will shutdown shortly",
            }),
          );
        } catch (err) {
          handlePopupFlagError(err);
        }
      },
      "Are you sure you want to shutdown this device?",
      "Shutdown device",
    );
  };

  const refreshStorageScreen = () => {
    return sendRemoteControlCommand(
      async () => {
        try {
          await dispatch(
            refreshDeviceScreen({
              storageId: storage.id,
            }),
          ).unwrap();
          dispatch(
            showPopupFlag({
              appearance: "success",
              title: "Screen was refreshed",
            }),
          );
        } catch (err) {
          handlePopupFlagError(err);
        }
      },
      "Are you sure you want to refresh the toolbox screen?",
      "Refresh screen",
    );
  };

  const sendRemoteControlCommand = (callback: () => void, description: string, actionText = "Confirm") => {
    return () => {
      dispatch(
        openModal(Modals.CONFIRMATION, {
          description: description,
          actionText: actionText,
          actionColor: "primary",
          onConfirm: callback,
        }),
      );
    };
  };

  if (!storage) {
    return null;
  }

  return (
    <>
      <StageHeader title="Settings" />
      <DataGrid>
        <DataItem label="Toolbox code" value={<Text>{storage.id}</Text>} />
        <DataItem
          label="Name"
          value={
            <TextInput
              value={values.name}
              input={{ name: "name", placeholder: "Enter name" }}
              onChange={handleChange}
            />
          }
        />
        <DataItem
          label="Description"
          value={
            <TextAreaInput
              value={values.description}
              input={{
                name: "description",
                placeholder: "Enter description",
              }}
              onChange={handleChange}
            />
          }
        />
        <DataItem
          label="Toolbox"
          value={
            <DataItemGroup>
              <VSpace>
                <FieldGroup label="Model">
                  <SelectStorageModelInput
                    appearance="normal"
                    name="model"
                    value={values.model}
                    onChange={handleSelectChange}
                  />
                </FieldGroup>
                <FieldGroup label="Drawer names">
                  <DrawerNames storage={values} onChange={(value: FixLater) => handleChange("drawers", value)} />
                </FieldGroup>
                {/* Deliberately outside the online/commissioned branch below. Maglocks describe how a
                    toolbox is built and wired, so they have to be configurable before the box has ever
                    connected — commissioning is precisely when you set them. */}
                <FieldGroup label="Maglocks">
                  <CheckboxInput
                    input={{ name: "maglockEnabled" }}
                    label="Magnetic drawer locks"
                    value={values.config?.maglockEnabled ?? false}
                    onChange={handleConfigChange}
                  />
                  {values.config?.maglockEnabled && (
                    <InputGroup label="Hold delay (ms)">
                      <NumberInput
                        value={values.config?.maglockDelayMs}
                        input={{
                          name: "maglockDelayMs",
                          step: "1",
                          min: 0,
                          max: MAGLOCK_DELAY_MAX_MS,
                          placeholder: "1000",
                        }}
                        onChange={handleMaglockDelayChange}
                      />
                    </InputGroup>
                  )}
                </FieldGroup>
              </VSpace>
            </DataItemGroup>
          }
        />
        <DataItem
          label="Mode"
          value={
            <SelectStorageModeInput
              appearance="normal"
              name="modeId"
              value={values.modeId}
              onChange={handleSelectChange}
            />
          }
        />
        <RestrictByApp app={Apps.MODBUS}>
          <DataItem
            label="Modbus server port"
            value={
              <NumberInput
                value={values.modbusPort}
                input={{
                  name: "modbusPort",
                  placeholder: "Enter Modbus server port number (default: 502)",
                }}
                onChange={handleChange}
              />
            }
          />
        </RestrictByApp>
        <DataItem
          label="Location"
          value={
            <LocationSelector
              appearance="normal"
              name="locationId"
              value={values.locationId}
              onChange={changeLocation}
            />
          }
        />
        {values.config && (
          <DataItem
            label="Timezone"
            value={
              <SelectInput
                input={{
                  type: "select",
                  name: "timezone",
                  placeholder: "Select timezone",
                  labelKey: "label",
                  valueKey: "value",
                  options: TIMEZONE_OPTIONS,
                }}
                value={values.config.timezone || ""}
                onChange={handleConfigSelectChange}
              />
            }
          />
        )}
        {storage.sentryApiKey?.dateActivated && (
          <DataItem
            label="Remote control"
            value={
              storage.online ? (
                <DataItemGroup>
                  <VSpace gap="gap-2">
                    <FieldGroup label="Drawers">
                      <SecondaryButton fitContent icon="/images/icons/lock_5.svg" onClick={lock()}>
                        Lock device
                      </SecondaryButton>
                      <SecondaryButton fitContent icon="/images/icons/unlock_2.svg" onClick={unlock()}>
                        Unlock device
                      </SecondaryButton>
                    </FieldGroup>
                    <FieldGroup label="Power options">
                      <SecondaryButton fitContent icon="/images/icons/restart_1.svg" onClick={reboot()}>
                        Reboot
                      </SecondaryButton>
                      <SecondaryButton fitContent icon="/images/icons/power_1.svg" onClick={powerOff()}>
                        Power off
                      </SecondaryButton>
                    </FieldGroup>
                    <FieldGroup label="Miscellaneous">
                      <SecondaryButton fitContent icon="/images/icons/loading.svg" onClick={refreshStorageScreen()}>
                        Refresh screen
                      </SecondaryButton>
                    </FieldGroup>
                    {isAdmin && (
                      <FieldGroup label="Diagnostics">
                        <SecondaryButton fitContent icon="/images/icons/system_config_1.svg" onClick={openShell}>
                          Open shell
                        </SecondaryButton>
                        <SecondaryButton fitContent icon="/images/icons/dashboard_1.svg" onClick={openSensorHealth}>
                          Sensor health
                        </SecondaryButton>
                      </FieldGroup>
                    )}
                    <FieldGroup label="Unlink toolbox">
                      <WarningButton fitContent icon="/images/icons/unlink_1.svg" onClick={handleUnlinkStorage}>
                        Unlink toolbox
                      </WarningButton>
                    </FieldGroup>
                  </VSpace>
                </DataItemGroup>
              ) : (
                <DataItemGroup>
                  <FieldGroup label="Commands">
                    <Paragraph muted>Toolbox must be online to access remote control commands.</Paragraph>
                  </FieldGroup>
                  <FieldGroup label="Unlink toolbox">
                    <WarningButton fitContent icon="/images/icons/unlink_1.svg" onClick={handleUnlinkStorage}>
                      Unlink Toolbox
                    </WarningButton>
                  </FieldGroup>
                </DataItemGroup>
              )
            }
          />
        )}
      </DataGrid>
    </>
  );
};
